"use client";

import { useRef } from "react";
import { SectionHead, SectionShell, useBandRowAlign, useWholeRowBlocks } from "@/components/sections/kit";
import {
  CATALOG,
  CATEGORIES,
  STATUS_META,
  depthLabel,
  integrationHref,
  statusLabel,
  type Integration,
} from "./data";

/** Row layout: tile · name+detail · what crosses the wire · status · chevron.
 *  The wire column only earns its place once there is room (lg+); below that
 *  it drops under the name, and below md the row stops being a grid at all. */
const ROW_GRID =
  "grid grid-cols-[44px_minmax(0,1fr)] items-start gap-x-4 gap-y-3 " +
  "md:grid-cols-[44px_minmax(0,1fr)_92px_18px] md:items-center md:gap-[18px] " +
  "lg:grid-cols-[44px_minmax(0,1fr)_360px_92px_18px]";

function MarkTile({ item }: { item: Integration }) {
  const { mark } = item;
  if (mark.kind === "brand") {
    const { Icon } = mark;
    return (
      <span className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] border border-line-2 bg-surface">
        {/* color="default" = the vendor's own brand color, bundled locally. */}
        <Icon size={21} color="default" aria-hidden="true" />
      </span>
    );
  }
  const { Icon, tint } = mark;
  return (
    <span
      className={`flex h-11 w-11 flex-none items-center justify-center rounded-[11px] border ${
        tint ? "border-line-2 bg-surface" : "border-brand-600/[0.18] bg-brand-600/[0.07]"
      }`}
    >
      <Icon size={20} strokeWidth={1.8} aria-hidden="true" style={{ color: tint ?? "var(--color-brand-600)" }} />
    </span>
  );
}

function StatusPill({ item }: { item: Integration }) {
  const c = STATUS_META[item.status].color;
  return (
    <span
      className="col-start-2 justify-self-start whitespace-nowrap rounded-full border px-2.5 py-[3px] font-jetbrains text-[10.5px] md:col-start-auto md:justify-self-end"
      style={{
        color: c,
        background: `color-mix(in srgb, ${c} 10%, white)`,
        borderColor: `color-mix(in srgb, ${c} 35%, transparent)`,
      }}
    >
      {statusLabel(item)}
    </span>
  );
}

function CatalogRow({ item, first }: { item: Integration; first: boolean }) {
  const href = integrationHref(item);

  const body = (
    <div
      className={`${ROW_GRID} px-5 py-4 transition-colors group-hover:bg-surface-2 md:px-[22px] ${
        first ? "" : "border-t border-line"
      }`}
    >
      <MarkTile item={item} />

      <span className="min-w-0">
        <span className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
          <span className="text-[15px] font-bold tracking-[-0.01em] text-ink">{item.name}</span>
          <span className="font-jetbrains text-[10px] uppercase tracking-[0.1em] text-dim">
            {depthLabel(item)}
          </span>
        </span>
        <span className="mt-[3px] block text-[13px] leading-[1.5] text-muted [text-wrap:pretty]">
          {item.detail}
        </span>
        {/* Below lg the wire line lives here instead of its own column. */}
        <span className="mt-2 block font-jetbrains text-[12px] leading-[1.6] text-ink/80 lg:hidden">
          {item.flow}
        </span>
      </span>

      <span className="hidden truncate font-jetbrains text-[12px] leading-[1.6] text-ink/80 lg:block">
        {item.flow}
      </span>

      <StatusPill item={item} />

      <span aria-hidden className="hidden text-[14px] text-dim md:block">
        {href ? "→" : ""}
      </span>
    </div>
  );

  if (!href) return <li className="group">{body}</li>;

  return (
    <li className="group">
      <a
        href={href}
        className="block outline-none focus-visible:bg-surface-2 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-600/40"
        aria-label={`${item.name} — ${item.flow}`}
      >
        {body}
      </a>
    </li>
  );
}

/**
 * The catalog — the page's bulk and its honesty surface. One block per category,
 * each a labelled white sheet of rows spelling out what crosses the wire and
 * how deep the connection goes. Every block is a `[data-grid-block]` with the
 * DEFAULT page fill: the block covers the category label *and* the sheet, and
 * the sheet paints its own white on top of the cleared cells — that keeps the
 * design's "label outside the card, hairline running right" while a block
 * still carries exactly one fill.
 *
 * Static apart from CSS hover, but a client component because the layout
 * hooks measure it.
 */
export default function CatalogDirectory() {
  const rootRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);

  return (
    <SectionShell id="catalog" className="scroll-mt-[var(--grid-cell,72px)]">
      {/* flex column, not a plain block: with eight [data-grid-block]s in one
          section, CSS margin collapsing (SectionHead's mb-[54px] swallowing
          the row aligner's first correction) made the aligner overshoot one
          category by a whole row. A flex container never collapses margins,
          so every correction lands on the first pass. */}
      <div ref={rootRef} className="flex flex-col max-md:gap-10">
        <SectionHead
          eyebrow="The catalog"
          title="Everything on the wire, spelled out."
          lead="What connects, what flows which direction, and how deep the setup goes — honestly labeled: Live works today, Roadmap carries its date."
        />

        {CATEGORIES.map((cat) => {
          const entries = CATALOG.filter((i) => i.category === cat.id);
          if (!entries.length) return null;
          return (
            <div key={cat.id} id={cat.id} data-grid-block className="scroll-mt-[calc(2*var(--grid-cell,72px))]">
              <div className="mb-[13px] flex items-center gap-2.5">
                <span
                  aria-hidden
                  className="h-2 w-2 flex-none rounded-full"
                  style={{ background: cat.color }}
                />
                <h3 className="font-jetbrains text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">
                  {cat.label}
                </h3>
                <span className="font-jetbrains text-[11px] text-dim">{entries.length}</span>
                <span aria-hidden className="h-px flex-1 bg-line-2" />
              </div>

              <ul className="overflow-hidden rounded-ref-lg border border-line bg-surface shadow-[0_14px_40px_-16px_rgba(16,16,40,0.09)]">
                {entries.map((item, i) => (
                  <CatalogRow key={item.id} item={item} first={i === 0} />
                ))}
              </ul>
            </div>
          );
        })}

        <div data-grid-block className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
          <span className="text-[14.5px] font-semibold text-dim">Yours missing?</span>
          <span className="text-[13.5px] text-muted">
            <a href="#request" className="font-medium text-brand-600 underline-offset-2 hover:underline">
              Request it below
            </a>{" "}
            — we read every one; meanwhile the REST API and click stream let your own code reach it, for links you create over the API.
          </span>
        </div>
      </div>
    </SectionShell>
  );
}
